import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Plus, Eye, Info, FileText, Settings2, X, AlertTriangle, Check, Ban, Clock } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";
import LeaveForm, { MAROON, inpL, TYPE_LABEL, DUR_LABEL, ST_LABEL, ST_CHIP, TYPE_CHIP } from "@/components/kiyana/LeaveForm";

const card = "bg-white rounded-2xl border border-slate-100";
const TABS = ["semua", "menunggu", "disetujui", "ditolak", "dibatalkan"];
const TAB_LABEL = { semua: "Semua", menunggu: "Menunggu", disetujui: "Disetujui", ditolak: "Ditolak", dibatalkan: "Dibatalkan" };

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function durText(l) {
  const base = DUR_LABEL[l.duration] || l.duration;
  if (l.duration === "hours" && l.hours_start) return `${base} ${l.hours_start} - ${l.hours_end}`;
  if (l.duration === "full") return l.start_date === l.end_date ? "1 hari" : `${l.workdays || ""} hari kerja`;
  return base;
}

function SettingsModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ quota_per_year: 12, hours_unit: "prorata" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/leave/meta").then((r) => setForm(r.data.settings)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/leave/settings", { quota_per_year: parseInt(form.quota_per_year) || 0, hours_unit: form.hours_unit });
      toast.success("Pengaturan izin tersimpan"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="leave-settings-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-sm rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-slate-900">Pengaturan Izin</h3>
          <button data-testid="leave-settings-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Kuota saldo izin per tahun (hari)</label>
          <input data-testid="leave-settings-quota" type="number" min="0" max="60" value={form.quota_per_year} onChange={(e) => setForm({ ...form, quota_per_year: e.target.value })} className={inpL} />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Satuan saldo untuk izin Jam Tertentu</label>
          <select data-testid="leave-settings-unit" value={form.hours_unit} onChange={(e) => setForm({ ...form, hours_unit: e.target.value })} className={inpL}>
            <option value="prorata">Prorata (jam izin ÷ jam kerja bersih)</option>
            <option value="setengah_hari">Dibulatkan ke 0,5 hari terdekat ke atas</option>
          </select>
        </div>
        <button data-testid="leave-settings-save" onClick={save} disabled={busy} className="w-full h-10 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan"}</button>
      </div>
    </div>
  );
}

export default function AdminLeave() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(nowMonth());
  const [type, setType] = useState("");
  const [role, setRole] = useState("");
  const [empIds, setEmpIds] = useState([]);
  const [tab, setTab] = useState("menunggu");
  const [selId, setSelId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [paid, setPaid] = useState(null);
  const [note, setNote] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/leave", { params: { period, type: type || undefined, role: role || undefined, employee_id: empIds.join(",") || undefined } })
      .then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat data izin"); });
  }, [period, type, role, empIds]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => (data?.items || []).filter((i) => tab === "semua" || i.status === tab), [data, tab]);
  const { paged: pagedLeave, pager: leavePager } = usePager(items);
  const counts = data?.counts || {};
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0);
  const sel = (data?.items || []).find((i) => i.id === selId) || null;
  useEffect(() => { setPaid(null); setNote(""); setConfirmCancel(false); }, [selId]);

  const decide = async (action) => {
    if (!sel) return;
    if (action === "approve" && paid === null) return toast.error("Pilih perlakuan gaji: Dibayar atau Potong Gaji");
    if (action === "reject" && note.trim().length < 3) return toast.error("Catatan penolakan wajib diisi");
    setBusy(true);
    try {
      const r = await api.put(`/leave/${sel.id}/${action}`, action === "approve" ? { paid, note: note.trim() } : { note: note.trim() });
      toast.success(action === "approve" ? "Izin disetujui — absensi diperbarui" : "Pengajuan ditolak");
      if (r.data?.payroll_adjustment_needed) toast.warning("Payroll periode terkait sudah dibayar — lakukan penyesuaian manual");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); } finally { setBusy(false); }
  };

  const cancelLeave = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/leave/${sel.id}/cancel`);
      toast.success("Izin dibatalkan — saldo dikembalikan & absensi diperbarui");
      if (r.data?.payroll_adjustment_needed) toast.warning("Payroll periode terkait sudah dibayar — perlu penyesuaian manual");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); } finally { setBusy(false); setConfirmCancel(false); }
  };

  const workdays = (sel?.days || []).filter((d) => d.workday);
  return (
    <div data-testid="leave-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Izin Karyawan</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola pengajuan izin, sakit, dan cuti karyawan.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="leave-settings-btn" onClick={() => setShowSettings(true)} className="w-10 h-10 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-[#7F1D2D] hover:border-[#7F1D2D]/30 transition-colors"><Settings2 className="w-4 h-4" /></button>
          <button data-testid="leave-create-btn" onClick={() => setShowForm(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 transition-colors" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Catat Izin</button>
        </div>
      </div>

      <div className={`${card} p-4 grid grid-cols-2 lg:grid-cols-4 gap-3`}>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Periode</label>
          <input data-testid="leave-filter-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inpL} />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Jenis</label>
          <select data-testid="leave-filter-type" value={type} onChange={(e) => setType(e.target.value)} className={inpL}>
            <option value="">Semua Jenis</option>
            {Object.entries(TYPE_LABEL).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Divisi</label>
          <select data-testid="leave-filter-role" value={role} onChange={(e) => setRole(e.target.value)} className={inpL}>
            <option value="">Semua Divisi</option>
            {(data?.options?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Karyawan</label>
          <EmpPicker tid="leave-filter-emp" options={data?.options?.employees || []} value={empIds} onChange={setEmpIds} placeholder="Ketik nama karyawan" />
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-100 overflow-x-auto">
        {TABS.map((t) => {
          const n = t === "semua" ? totalAll : (counts[t] || 0);
          return (
            <button key={t} data-testid={`leave-tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${tab === t ? "border-[#7F1D2D] text-[#7F1D2D]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {TAB_LABEL[t]} <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t ? "bg-[#7F1D2D] text-white" : "bg-slate-100 text-slate-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-5 items-start">
        <div className="space-y-4">
          <div className={`${card} overflow-hidden`}>
            <div className="px-5 py-4 border-b border-slate-50">
              <h3 className="font-display text-sm font-bold text-slate-900">Daftar Pengajuan</h3>
              <p className="text-[10px] text-slate-400">{items.length} pengajuan {TAB_LABEL[tab].toLowerCase()}.</p>
            </div>
            <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.9fr_0.7fr] gap-2 px-5 py-2.5 text-[9px] uppercase tracking-wider text-slate-400 border-b border-slate-50">
              <span>Karyawan</span><span>Jenis</span><span>Tanggal</span><span>Durasi</span><span>Status</span><span className="text-right">Aksi</span>
            </div>
            {!data && <div className="py-10 text-center text-xs text-slate-400">Memuat...</div>}
            {data && !items.length && <div data-testid="leave-empty" className="py-10 text-center text-xs text-slate-400">Tidak ada pengajuan pada filter ini.</div>}
            {pagedLeave.map((l) => (
              <div key={l.id} data-testid={`leave-row-${l.id}`} className={`grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_0.8fr_0.9fr_0.7fr] gap-2 px-5 py-3.5 items-center border-b border-slate-50 last:border-0 transition-colors ${selId === l.id ? "bg-[#FBF3F4]" : "hover:bg-slate-50/50"}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center text-xs font-bold flex-shrink-0">{(l.user_name || "?")[0]}</span>
                  <div className="min-w-0"><div className="text-xs font-semibold text-slate-800 truncate">{l.user_name}</div><div className="text-[9px] text-slate-400">{l.employee_id || "—"}</div></div>
                </div>
                <div><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${TYPE_CHIP[l.type] || "bg-slate-100 text-slate-500"}`}>{TYPE_LABEL[l.type] || l.type}</span></div>
                <div className="text-[11px] text-slate-600">{formatDateID(l.start_date)}{l.end_date !== l.start_date ? ` – ${formatDateID(l.end_date)}` : ""}</div>
                <div className="text-[11px] text-slate-600">{durText(l)}</div>
                <div><span data-testid={`leave-status-${l.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[l.status]}`}>{ST_LABEL[l.status]}</span></div>
                <div className="text-right"><button data-testid={`leave-detail-btn-${l.id}`} onClick={() => setSelId(l.id)} className="h-8 px-3 rounded-lg border text-[11px] font-semibold transition-colors" style={{ borderColor: MAROON, color: MAROON }}>Detail</button></div>
              </div>
            ))}
            <Pager {...leavePager} testid="leave-pager" />
          </div>

          <div className={`${card} p-5`}>
            <h3 className="font-display text-sm font-bold text-slate-900 mb-4">Alur Pengajuan</h3>
            <div className="grid grid-cols-3 gap-3">
              {[["Ajukan Izin", "Karyawan mengajukan izin melalui sistem."], ["Ditinjau HR", "Tim HR meninjau dan memverifikasi pengajuan."], ["Absensi Diperbarui", "Setelah disetujui, absensi akan ditandai sesuai jenis izin."]].map(([t, d], i) => (
                <div key={t} className="flex sm:flex-row flex-col gap-2.5">
                  <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: MAROON }}>{i + 1}</span>
                  <div><div className="text-xs font-bold text-slate-800">{t}</div><div className="text-[10px] text-slate-400 mt-0.5">{d}</div></div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-xl p-3">
              <Info className="w-3.5 h-3.5 flex-shrink-0" /> Pengajuan menunggu belum mengubah absensi atau gaji.
            </div>
          </div>
        </div>

        <div className={`${card} p-5 space-y-4 lg:sticky lg:top-4`} data-testid="leave-detail-panel">
          {!sel && <div className="py-16 text-center text-xs text-slate-400">Pilih pengajuan untuk melihat detail.</div>}
          {sel && (
            <>
              <div className="flex items-start justify-between">
                <h3 className="font-display text-sm font-bold text-slate-900">Detail Pengajuan</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[sel.status]}`}>{ST_LABEL[sel.status]}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center text-base font-bold">{(sel.user_name || "?")[0]}</span>
                <div><div className="text-sm font-bold text-slate-900">{sel.user_name}</div><div className="text-[10px] text-slate-400">{sel.employee_id || "—"} • {sel.role || ""}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 text-[11px] border-t border-slate-50 pt-3">
                <div className="text-slate-400">Jenis Izin</div><div className="text-right font-semibold text-slate-700">{TYPE_LABEL[sel.type] || sel.type}</div>
                <div className="text-slate-400">Tanggal</div><div className="text-right font-semibold text-slate-700">{formatDateID(sel.start_date)}{sel.end_date !== sel.start_date ? ` – ${formatDateID(sel.end_date)}` : ""}</div>
                <div className="text-slate-400">Durasi</div><div className="text-right font-semibold text-slate-700">{durText(sel)}</div>
                <div className="text-slate-400">Saldo Terpakai</div><div className="text-right font-semibold text-slate-700">{sel.leave_units ?? "—"} hari</div>
              </div>
              {workdays.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Shift & Jam Terdampak</div>
                  {workdays.map((d) => (
                    <div key={d.date} className="bg-slate-50/70 rounded-lg px-2.5 py-2 text-[10px]">
                      <div className="flex justify-between font-semibold text-slate-600"><span>{formatDateID(d.date)}</span><span className="text-slate-400">{d.shift}{d.break ? ` • istirahat ${d.break}` : ""}</span></div>
                      <div className="flex justify-between mt-0.5"><span className="text-red-500">Izin {d.leave_start ? `${d.leave_start}-${d.leave_end}` : "penuh"}</span><span className="text-emerald-600">Hadir {d.required_start ? `${d.required_start}-${d.required_end}` : "dibebaskan"}</span></div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Alasan</div>
                <div className="bg-slate-50 rounded-lg p-3 text-[11px] text-slate-600">{sel.reason}</div>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-400 font-semibold">Lampiran</span>
                {sel.proof_url
                  ? <a data-testid="leave-proof-link" href={sel.proof_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sky-600 font-semibold hover:underline"><FileText className="w-3.5 h-3.5" /> dokumen <Eye className="w-3 h-3" /> Lihat</a>
                  : <span className="text-slate-300">Tidak ada</span>}
              </div>
              {sel.payroll_adjustment_needed && (
                <div data-testid="leave-payroll-flag" className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Payroll periode terkait sudah dibayar — perubahan izin ini perlu penyesuaian manual.
                </div>
              )}

              {sel.status === "menunggu" && (
                <div className="space-y-3 border-t border-slate-50 pt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Keputusan HR</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="leave-decision-paid" onClick={() => setPaid(true)}
                      className={`h-11 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${paid === true ? "border-[#7F1D2D] bg-[#FBF3F4] text-[#7F1D2D]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${paid === true ? "border-[#7F1D2D]" : "border-slate-300"}`}>{paid === true && <span className="w-1.5 h-1.5 rounded-full bg-[#7F1D2D]" />}</span> Dibayar
                    </button>
                    <button data-testid="leave-decision-deduct" onClick={() => setPaid(false)}
                      className={`h-11 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${paid === false ? "border-[#7F1D2D] bg-[#FBF3F4] text-[#7F1D2D]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${paid === false ? "border-[#7F1D2D]" : "border-slate-300"}`}>{paid === false && <span className="w-1.5 h-1.5 rounded-full bg-[#7F1D2D]" />}</span> Potong Gaji
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-400">{paid === false ? "Potong Gaji: masuk komponen Potongan Izin di payroll." : "Dibayar: tidak ada potongan gaji."}</p>
                  <textarea data-testid="leave-decision-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Tambahkan catatan keputusan (wajib saat menolak)..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
                  <div className="flex items-center gap-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-xl p-3">
                    <Info className="w-3.5 h-3.5 flex-shrink-0" /> Absensi akan ditandai izin setelah disetujui.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="leave-reject-btn" onClick={() => decide("reject")} disabled={busy} className="h-11 rounded-xl border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-40">Tolak</button>
                    <button data-testid="leave-approve-btn" onClick={() => decide("approve")} disabled={busy || paid === null} className="h-11 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Memproses..." : "Setujui Izin"}</button>
                  </div>
                </div>
              )}

              {sel.status === "disetujui" && (
                <div className="space-y-3 border-t border-slate-50 pt-3">
                  <div className="grid grid-cols-2 gap-y-2 text-[11px]">
                    <div className="text-slate-400">Perlakuan Gaji</div>
                    <div className={`text-right font-bold ${sel.paid ? "text-emerald-600" : "text-red-500"}`}>{sel.paid ? "Dibayar" : "Potong Gaji"}</div>
                    <div className="text-slate-400">Diputuskan oleh</div><div className="text-right font-semibold text-slate-700">{sel.decided_by || "—"}</div>
                  </div>
                  {sel.hr_note && <div className="bg-slate-50 rounded-lg p-3 text-[11px] text-slate-600">Catatan HR: {sel.hr_note}</div>}
                  {!confirmCancel
                    ? <button data-testid="leave-cancel-btn" onClick={() => setConfirmCancel(true)} className="w-full h-10 rounded-xl border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 transition-colors flex items-center justify-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Batalkan Izin (kembalikan saldo & absensi)</button>
                    : <div className="grid grid-cols-2 gap-2">
                        <button data-testid="leave-cancel-no" onClick={() => setConfirmCancel(false)} className="h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold">Tidak Jadi</button>
                        <button data-testid="leave-cancel-yes" onClick={cancelLeave} disabled={busy} className="h-10 rounded-xl bg-red-500 text-white text-xs font-semibold disabled:opacity-40">{busy ? "Memproses..." : "Ya, Batalkan"}</button>
                      </div>}
                </div>
              )}
              {sel.status === "ditolak" && sel.hr_note && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-[11px] text-red-600 border-t">Alasan penolakan: {sel.hr_note}</div>
              )}

              <div className="border-t border-slate-50 pt-3 space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Jejak Pengajuan</div>
                {(sel.history || []).map((h, i) => (
                  <div key={i} className="text-[10px] text-slate-500 flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-slate-300 flex-shrink-0" />
                    <span className="capitalize font-semibold">{h.action}</span> oleh {h.by} • {formatDateID((h.at || "").slice(0, 10))}{(h.at || "")[11] ? ` ${(h.at || "").slice(11, 16)}` : ""}{h.note ? ` — ${h.note}` : ""}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showForm && <LeaveForm tid="leavef" emps={data?.options?.employees || []} onClose={() => setShowForm(false)} onSaved={load} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} onSaved={load} />}
    </div>
  );
}
